import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, BookmarkPlus, BookmarkCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { normalizeOpenLibraryId, isUuid } from '@/lib/searchUtils';
import { autoCategorizeTBRBook } from '@/lib/autoCategorizeTBR';

interface BookData {
  id?: string;
  open_library_id?: string;
  title: string;
  authors: string[];
  cover_url?: string;
  isLocal?: boolean;
}

interface ReviewTBRButtonsProps {
  book: BookData;
  className?: string;
  onTBRAdd?: (bookId: string) => void;
  onRatingClick?: (bookId: string) => void;
}

export function ReviewTBRButtons({ book, className = '', onTBRAdd, onRatingClick }: ReviewTBRButtonsProps) {
  const [isInTBR, setIsInTBR] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if book is in TBR on mount
  useEffect(() => {
    if (user && book.id && isUuid(book.id)) {
      checkTBRStatus();
    }
  }, [user, book.id]);

  const checkTBRStatus = async () => {
    if (!user || !book.id || !isUuid(book.id)) return;
    
    try {
      // Check if book is in user's TBR shelf
      const { data: userBooks } = await supabase
        .from('user_books')
        .select('shelf')
        .eq('user_id', user.id)
        .eq('book_id', book.id)
        .eq('shelf', 'to-read')
        .maybeSingle();

      setIsInTBR(!!userBooks);
    } catch (error) {
      console.error('Error checking TBR status:', error);
    }
  };

  const handleReview = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      // If it's a local book, navigate directly
      if (book.isLocal && book.id) {
        onRatingClick?.(book.id);
        navigate(`/book/${book.id}`);
        return;
      }

      // For Open Library books, upsert them first
      const normalizedId = normalizeOpenLibraryId(book.open_library_id || '');
      if (!normalizedId) {
        throw new Error('Invalid book identifier');
      }

      const { data: existingBook, error: searchError } = await supabase
        .from('books')
        .select('id')
        .or(`open_library_id.eq.${normalizedId},open_library_id.eq./works/${normalizedId}`)
        .maybeSingle();

      if (searchError) throw searchError;

      if (existingBook) {
        navigate(`/book/${existingBook.id}`);
        return;
      }

      // Create new book
      const { data: newBook, error } = await supabase
        .from('books')
        .insert({
          open_library_id: normalizedId,
          title: book.title,
          authors: book.authors || [],
          cover_url: book.cover_url,
          source: 'openlibrary',
        })
        .select('id')
        .single();

      if (error) throw error;
      if (!newBook?.id) throw new Error('Failed to create book');

      navigate(`/book/${newBook.id}`);
    } catch (error) {
      console.error('Error handling review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Could not open book for review.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTBR = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      let bookId = book.id;

      // If it's not a local book, upsert it first
      if (!book.isLocal) {
        const normalizedId = normalizeOpenLibraryId(book.open_library_id || '');
        const { data: existingBook } = await supabase
          .from('books')
          .select('id')
          .or(`open_library_id.eq.${normalizedId},open_library_id.eq./works/${normalizedId}`)
          .maybeSingle();

        if (existingBook) {
          bookId = existingBook.id;
        } else {
          const { data: newBook, error } = await supabase
            .from('books')
            .insert({
              open_library_id: normalizedId,
              title: book.title,
              authors: book.authors,
              cover_url: book.cover_url,
              source: 'openlibrary',
            })
            .select('id')
            .single();

          if (error) throw error;
          bookId = newBook.id;
        }
      }

      if (isInTBR) {
        // Check if book has been read/rated before removing from TBR
        const { data: existingEntry } = await supabase
          .from('user_books')
          .select('finished_at, rating_star, review_text')
          .eq('user_id', user.id)
          .eq('book_id', bookId)
          .single();

        if (existingEntry && (existingEntry.finished_at || existingEntry.rating_star)) {
          // Don't delete - just update shelf to 'read' and ensure finished_at is set
          const finishedDate = existingEntry.finished_at || new Date().toISOString().split('T')[0];
          await supabase
            .from('user_books')
            .update({ 
              shelf: 'read',
              finished_at: finishedDate
            })
            .eq('user_id', user.id)
            .eq('book_id', bookId);

          toast({
            title: "Moved to Read Shelf",
            description: "Book moved from TBR to your Read shelf since you've already read/reviewed it.",
          });
        } else {
          // Safe to delete - no read data exists
          await supabase
            .from('user_books')
            .delete()
            .eq('user_id', user.id)
            .eq('book_id', bookId);

          toast({
            title: "Removed from TBR",
            description: "Book removed from your To Be Read list.",
          });
        }
        
        setIsInTBR(false);
      } else {
        // Add to TBR
        await supabase
          .from('user_books')
          .upsert({
            user_id: user.id,
            book_id: bookId,
            shelf: 'to-read',
            privacy: 'private', // Changed to private default
          }, {
            onConflict: 'user_id,book_id'
          });

        setIsInTBR(true);
        toast({
          title: "Added to TBR",
          description: "Book added to your To Be Read list.",
        });

        // Auto-categorize the TBR book
        if (bookId) {
          autoCategorizeTBRBook(user.id, bookId).catch(err => 
            console.warn('Auto-categorization failed:', err)
          );
          onTBRAdd?.(bookId);
        }
      }
    } catch (error) {
      console.error('Error handling TBR:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update your TBR list.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <Button
        size="sm"
        variant="default"
        onClick={handleReview}
        disabled={loading}
        className="flex-1"
      >
        <Star className="w-4 h-4 mr-1" />
        Review
      </Button>
      
      <Button
        size="sm"
        variant={isInTBR ? "secondary" : "outline"}
        onClick={handleTBR}
        disabled={loading}
        className="flex-1"
      >
        {isInTBR ? (
          <>
            <BookmarkCheck className="w-4 h-4 mr-1" />
            In TBR
          </>
        ) : (
          <>
            <BookmarkPlus className="w-4 h-4 mr-1" />
            TBR
          </>
        )}
      </Button>
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export default memo(ReviewTBRButtons);